<template>
  <div class="container">
    <div class="dashboard-urls__title-container">
    <base-more-details-link  to="/url-planner"/>
  </div>
    <div class="dashboard-urls__loader" v-if="dataFetchingCount !== 0">
      <base-loader/>
    </div>
    <div class="common-table dashboard-urls__table" v-else>
      <div class="common-table__row common-table__header">
        <div class="common-table__header-cell"
             v-for="column in urlPlanningTableColumnsConfig"
             :key="column.property"
             :class="{[`dashboard-urls__${column.property}`]: true}">
          <div class="dashboard-urls__header"/>
          {{column.label}}
        </div>
      </div>
      <template v-if="dataMissing">
        <div class="common-table__row no-data-block">
          Awaiting Data
        </div>
      </template>
      <template v-else>
        <div class="common-table__row"
             v-for="(url) in sortedUrls"
             :key="url._id">
          <div class="common-table__cell"
               v-for="column in urlPlanningTableColumnsConfig"
               :key="column.property"
               :class="[`dashboard-urls__${column.property}`, `dashboard-urls__row_${column.property}`]">
            <template v-if="column.isSelectableValue">
              <base-select
                class="dashboard-urls__select"
                :options="column.options"
                border-less
                alignLeft
                :titleStyle="{'padding-left': '5px'}"
                showTopBorder
                shiftLabelRight
                v-model="url.currentUrlStatus"
                :selectStyle="{height: '60px!important'}"
                @change="openModal(url)"/>
            </template>
            <template v-else-if="column.isLink">
              <a class="dashboard-urls__link" target="_blank" :href="getColumnData(url, column.property)"
                 v-tooltip.bottom-start="{content: getColumnData(url, column.property), ...tooltipOptions}">
                {{getColumnData(url, column.property)}}
              </a>
            </template>
            <template v-else-if="column.property === 'target_date'">
              <div class="dashboard-urls__date_wrapper">
                {{getColumnData(url, column.property) | formattedDate}}
              </div>
            </template>
            <template v-else-if="['msv', 'keywords'].includes(column.property)">
              <div class="dashboard-urls__number">
                {{getColumnData(url, column.property) | commaDividedNumber}}
              </div>
            </template>
            <template v-else>
              <div class="dashboard-urls__label"
                   v-tooltip.bottom-start="{content: getColumnData(url, column.property), ...tooltipOptions}">
                {{getColumnData(url, column.property)}}
              </div>
            </template>
          </div>
        </div>
      </template>
      <div class="" v-if="isDataUpdating">
        <div class="dashboard-urls__update-loader">
          <base-loader/>
        </div>
      </div>
    </div>
    <modal name="status"
           v-bind="changeStatusModalProps"
    >
      <UrlStatusPopup
        :url="changingStatusData"
        :statuses="urlStatuses"
        @apply="statusApplied"
        @cancel="statusCancel()"/>
    </modal>
  </div>
</template>

<script>
import UrlStatusPopup from '@/features/url-planning/UrlStatusPopup'
import _ from 'lodash'
import filters from '@/common/Filters'
import helper from '@/services/Helper'
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import moment from 'moment'

const DEFAULT_CONFIG = [
  {
    label: 'Target Date',
    property: 'target_date',
    isSelectable: true,
    isDefaultSort: true
  },
  {
    label: 'URL Status',
    property: 'url_status',
    isSelectableValue: true,
    isSelectable: true,
    isSmartInput: false,
    options: []
  },
  {
    label: 'Brief Status',
    isSelectable: true,
    property: 'brief_status',
    isSmartInput: true,
    options: []
  },
  {
    label: 'Section',
    isSelectable: true,
    property: 'section',
    isSmartInput: true,
    options: []
  },
  {
    label: 'URL',
    isSelectable: true,
    property: 'url',
    isLink: true
  },
  {
    label: '# KW',
    property: 'keywords'
  },
  {
    label: 'Total MSV',
    property: 'msv'
  }
]
/**
 * It's component that is absent in final version of the application.
 * This one may be in demand in the future
 */
export default {
  name: 'DashboardUrls',
  components: { UrlStatusPopup },
  data () {
    return {
      dataFetchingCount: null,
      isDataUpdating: false,
      sortedUrls: [],
      changingStatusData: {},
      totalItems: 0,
      filterParams: { limit: 5,
        sort_column: 'target_date',
        sort_order: helper.DIRECTION_ASC
      },
      urlPlanningTableColumnsConfig: DEFAULT_CONFIG
    }
  },
  computed: {
    ...mapState([ 'selectedClient', 'urlStatuses' ]),
    ...mapState('urlPlanning', [ 'urls' ]),
    dataMissing () {
      return this.dataFetchingCount === 0 && !this.sortedUrls
    },
    changeStatusModalProps: function () {
      const changeStatusModalProps = {
        width: 690
      }
      return { ...baseModalProps, ...changeStatusModalProps }
    }
  },
  watch: {
    selectedClient (client, previousClient) {
      if (!previousClient) {
        // this way we try to avoid twice call loadPageData() from here and from urlStatus watcher by start app form UrlStatus page
        return
      }
      this.reset()
    },
    urls () {
      this.urls.forEach(item => {
        item.currentUrlStatus = item[ 'url_status' ]._id
        item.previousUrlStatus = item[ 'url_status' ]._id
      })
      this.sortedUrls = _.cloneDeep(this.urls)
    },
    urlStatuses: {
      handler () {
        if (!this.urlStatuses || !this.urlStatuses.length) {
          return
        }
        this.urlPlanningTableColumnsConfig.find(item => item.property === 'url_status').options = this.urlStatuses
          .map(item => {
            return {
              label: item.label,
              value: item._id
            }
          })

        this.filterParams.allowed_statuses = this.urlStatuses
          .filter(item => item.label.toLowerCase() === 'proposed')
          .map(item => {
            return item._id
          })
        this.loadPageData()
      },
      immediate: true
    }
  },
  filters: {
    commaDividedNumber: filters.commaDividedNumber,
    formattedDate: filters.formattedDate
  },
  methods: {
    ...mapActions('urlPlanning', [
      'getUrlPlannerData', 'resetData', 'changeStatus'
    ]),
    reset () {
      this.sortedUrls = []
      this.urlPlanningTableColumnsConfig = DEFAULT_CONFIG
      this.loadPageData()
    },

    getColumnData (url, property) {
      if (_.isObject(url[ property ])) {
        return _.get(url, [ property, 'name' ])
      }
      return url[ property ]
    },
    async loadUpdatedData () {
      this.isDataUpdating = true
      await this.getUrlPlannerData({ clientId: this.clientId, filters: this.filterParams })
      this.isDataUpdating = false
    },
    loadPageData: async function () {
      this.resetData()
      if (this.dataFetchingCount === null) {
        this.dataFetchingCount = 0
      }
      this.dataFetchingCount++
      await Promise.all([
        this.getUrlPlannerData({ clientId: this.clientId, filters: this.filterParams, clearData: true })
      ])
      this.dataFetchingCount--
    },
    openModal (url) {
      this.changingStatusData = url
      this.$modal.show('status')
    },
    async statusApplied ({ description, date }) {
      this.$modal.hide('status')
      if (moment(date).isValid()) {
        date = moment(date).format('YYYY-MM-DD')
      } else {
        date = null
      }
      try {
        this.isDataUpdating = true
        await this.changeStatus({
          clientId: this.clientId,
          urlId: this.changingStatusData._id,
          statusId: this.changingStatusData.currentUrlStatus,
          description,
          date
        })
        await this.loadUpdatedData()
        this.isDataUpdating = false
      } catch (error) {
        this.isDataUpdating = false
      }
    },
    statusCancel () {
      this.changingStatusData.currentUrlStatus = this.changingStatusData.previousUrlStatus
      this.$modal.hide('status')
    }
  }
}
</script>

<style scoped lang="scss">

  .common-table {
    border-radius: 4px;

    &__header {
      background-color: rgba(1, 22, 39, 0.1);
      border-top-right-radius: 6px;
      border-top-left-radius: 6px;
    }

    &__header-cell {
      color: $rfx-dark-march;
      font-weight: bold;
      padding: 0;
      height: 60px;
      display: flex;
      align-items: center;

      &:first-child {
        padding-left: 30px;
      }

      &:last-child {
        padding-right: 30px;
      }
    }

    &__cell {
      height: 60px;
      padding: 0;
      display: flex;
      align-items: center;
      font-size: 14px;
      border-bottom: 1px solid $rfx-light-steel;

    }

    &__row {
      border-left: 1px solid $rfx-light-steel;
      border-right: 1px solid $rfx-light-steel;

      &:last-child {
        border-bottom-left-radius: 6px;
        border-bottom-right-radius: 6px;
      }
    }
  }

  .dashboard-urls {

    &__title-container {
      display: flex;
      justify-content: space-between;
    }

    &__loader {
      display: flex;
      width: 100%;
      justify-content: center;
      align-items: center;
      height: 300px;
    }

    &__table {
      position: relative;
      display: flex;
      flex-direction: column;
    }

    &__update-loader {
      position: absolute;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      align-items: center;
      background-color: white;
      top: 0;
      opacity: 0.5;
    }

    &__number {
      padding-left: 15px;
    }
    &__date_wrapper {
      display: flex;
      flex-direction: column;
    }
    &__label {
      padding-left: 15px;
      overflow: hidden;
      @include ellipsis;
    }

    &__header {
      padding-left: 15px;
    }
    &__select {
      flex-basis: 100%;

    }
    &__no-data {
      display: flex;
      height: 300px;
      background-color: #fff;
      border-radius: $rfx-default-border-radius;
      align-items: center;
      justify-content: center;
      font-size: 27px;
      font-weight: 600;
      color: $rfx-medium-grey;
      flex: 1 1;
    }

    &__url {
      flex: 1 1;
      color: $rfx-dark-march;
    }

    &__target_date,
    &__url_status,
    &__brief_status,
    &__keywords,
    &__msv,
    &__section {
      flex: 0 0 150px;
      color: $rfx-light-march;
    }

    &__url_status {
      flex-basis: 140px;
      color: $rfx-dark-march;
      font-weight: 500;
    }

    &__keywords {
      flex-basis: 80px;
    }

    &__msv {
      flex-basis: 95px;
      padding-right: 30px;

    }

    &__section {
      flex-basis: 140px;
    }

    &__brief_status {
      flex-basis: 140px;

    }

    &__target_date {
      flex-basis: 140px;
      padding-left: 30px;
    }

    &__link {
      flex: 1 1;
      padding-left: 15px;
      @include ellipsis;
      text-decoration: none;
      color: $rfx-bright-blue;
    }

    &__row {
      &_section,
      &_brief_status,
      &_url {
        overflow: hidden;
      }
    }

    &__button {
      padding: 0 20px;
      height: 50px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      border: none;
      outline: none;
      cursor: pointer;
      background-color: $rfx-medium-march;
      color: #fff;
      margin: 24px 0 0;
    }
  }

  .no-data-block {
    border-bottom: 1px solid $rfx-light-steel;
  }

  .common-table {
    &__header-cell {
      color: $rfx-dark-march;
      font-weight: bold;
    }
  }
</style>
